import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardMedia,
  Avatar,
  Chip,
  Container,
  IconButton,
  Dialog,
  DialogContent,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Person as PersonIcon,
  Event as EventIcon,
  FlightTakeoff as TripIcon,
  Public as PublicIcon,
  PhotoLibrary as PhotoLibraryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { generateFingerprint } from '../../utils/browserFingerprint';
import { useUser } from '../../contexts/UserContext';

const StyledContainer = styled(Container)(({ theme }) => ({
   minHeight: '100vh',
   background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
   paddingTop: theme.spacing(4),
   paddingBottom: theme.spacing(4),
}));

const AlbumHeader = styled(Paper)(({ theme }) => ({
   padding: theme.spacing(3),
   marginBottom: theme.spacing(3),
   background: 'rgba(255, 255, 255, 0.95)',
   backdropFilter: 'blur(10px)',
   borderRadius: theme.spacing(2),
   boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
}));

const PhotoCard = styled(Card)(({ theme }) => ({
   cursor: 'pointer',
   transition: 'all 0.3s ease',
   borderRadius: theme.spacing(1),
   overflow: 'hidden',
   '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
   },
}));

const PhotoModal = styled(Dialog)(({ theme }) => ({
   '& .MuiDialog-paper': {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      maxWidth: '90vw',
      maxHeight: '90vh',
      margin: theme.spacing(2),
   },
}));

interface PublicAlbum {
   id: string;
   title: string;
   description: string;
   coverPhotoId: string | null;
   photoCount: number;
   createdAt: any;
   creator: {
      displayName: string;
      photoURL: string | null;
   } | null;
   source?: 'trip' | 'event' | 'manual';
   tripName?: string;
   eventName?: string;
}

interface Photo {
   id: string;
   url: string;
   thumbnailUrl: string;
   title: string;
   description: string;
   takenAt: any;
   size?: number;
   location?: any;
}

const PublicAlbumViewer: React.FC = () => {
   const { shareToken } = useParams<{ shareToken: string }>();
   const { currentUser } = useUser();
   const [album, setAlbum] = useState<PublicAlbum | null>(null);
   const [photos, setPhotos] = useState<Photo[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [modalOpen, setModalOpen] = useState(false);
   const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
   const [viewRecorded, setViewRecorded] = useState(false);

   useEffect(() => {
      if (!shareToken) return;

      const fetchSharedAlbum = async () => {
         try {
            setLoading(true);
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/shared/${shareToken}`
            );

            if (!response.ok) {
               const errorData = await response.json();
               throw new Error(errorData.message || 'Failed to load album');
            }

            const data = await response.json();
            setAlbum(data.album);
            setPhotos(data.photos || []);
         } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load album');
         } finally {
            setLoading(false);
         }
      };

      fetchSharedAlbum();
   }, [shareToken]);

   // Record album view
   useEffect(() => {
      const recordView = async () => {
         if (!album || !album.id) return;

         try {
            // Determine source from URL params
            const urlParams = new URLSearchParams(window.location.search);
            const source = urlParams.get('source') || 'shareable_link'; // Can be 'qr_scan' or 'shareable_link'

            // Check if view was already recorded in this session for this album+source
            // Using sessionStorage to prevent duplicate tracking on refresh
            const sessionKey = `album_view_recorded_${album.id}_${source}`;
            const alreadyRecordedInSession = sessionStorage.getItem(sessionKey);

            if (!viewRecorded && !alreadyRecordedInSession) {
               // Generate fingerprint for tracking
               const fingerprint = await generateFingerprint(true); // true = hardware-only mode
               console.log('Recording album view with fingerprint:', fingerprint);
               console.log('Album view source:', source);

               // Prepare viewer info
               const viewerInfo = currentUser
                  ? {
                       viewerId: currentUser.uid,
                       viewerName: currentUser.displayName || 'Unknown User',
                       viewerEmail: currentUser.email || null,
                       viewerPhotoURL: currentUser.photoURL || null,
                       userAgent: navigator.userAgent,
                    }
                  : {
                       viewerId: null,
                       viewerName: 'Anonymous',
                       viewerEmail: null,
                       viewerPhotoURL: null,
                       userAgent: navigator.userAgent,
                    };

               // Record view via API
               const response = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/record-view`,
                  {
                     method: 'POST',
                     headers: {
                        'Content-Type': 'application/json',
                     },
                     body: JSON.stringify({
                        fingerprint,
                        viewerInfo,
                        source,
                        userId: currentUser?.uid || null, // Also send userId for backend processing
                     }),
                  }
               );

               if (response.ok) {
                  const result = await response.json();
                  console.log('Album view recorded successfully:', result);
                  setViewRecorded(true);
                  // Mark as recorded in this session to prevent duplicate views on refresh
                  sessionStorage.setItem(sessionKey, 'true');
                  console.log('✅ View recorded and marked in session storage');
               }
            } else if (alreadyRecordedInSession) {
               console.log('ℹ️ View already recorded in this session, skipping duplicate');
            }
         } catch (error) {
            console.error('Error recording album view:', error);
         }
      };

      recordView();
   }, [album, currentUser, viewRecorded]);

   const openPhotoModal = (index: number) => {
      setCurrentPhotoIndex(index);
      setModalOpen(true);
   };

   const closePhotoModal = () => {
      setModalOpen(false);
   };

   const goToNextPhoto = () => {
      setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
   };

   const goToPrevPhoto = () => {
      setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
   };

   const getSourceIcon = (source?: string) => {
      switch (source) {
         case 'trip':
            return <TripIcon />;
         case 'event':
            return <EventIcon />;
         default:
            return <PersonIcon />;
      }
   };

   const getSourceLabel = (album: PublicAlbum) => {
      if (album.tripName) return album.tripName;
      if (album.eventName) return album.eventName;
      return 'Personal Album';
   };

   const formatDate = (timestamp: any) => {
      if (!timestamp) return '';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'long',
         day: 'numeric',
      });
   };

   if (loading) {
      return (
         <StyledContainer maxWidth='lg'>
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='50vh'>
               <CircularProgress size={60} sx={{ color: 'white' }} />
            </Box>
         </StyledContainer>
      );
   }

   if (error || !album) {
      return (
         <StyledContainer maxWidth='lg'>
            <Alert severity='error' sx={{ mb: 2 }}>
               {error || 'Album not found or no longer available'}
            </Alert>
         </StyledContainer>
      );
   }

   return (
      <StyledContainer maxWidth='lg'>
         {/* Album Header */}
         <AlbumHeader elevation={3}>
            <Box display='flex' alignItems='center' gap={2} mb={2}>
               <PhotoLibraryIcon color='primary' sx={{ fontSize: 40 }} />
               <Box flex={1}>
                  <Typography variant='h4' component='h1' gutterBottom>
                     {album.title}
                  </Typography>
                  <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                     <Chip
                        icon={getSourceIcon(album.source)}
                        label={getSourceLabel(album)}
                        size='small'
                        color='primary'
                        variant='outlined'
                     />
                     <Chip
                        icon={<PublicIcon />}
                        label='Public Album'
                        size='small'
                        color='success'
                        variant='outlined'
                     />
                     <Typography variant='body2' color='text.secondary'>
                        {album.photoCount} {album.photoCount === 1 ? 'photo' : 'photos'}
                     </Typography>
                  </Stack>
               </Box>
            </Box>

            {album.description && (
               <Typography variant='body1' color='text.secondary' paragraph>
                  {album.description}
               </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Box display='flex' alignItems='center' gap={2}>
               {album.creator && (
                  <Box display='flex' alignItems='center' gap={1}>
                     <Avatar
                        src={album.creator.photoURL || undefined}
                        sx={{ width: 32, height: 32 }}
                     >
                        {album.creator?.displayName?.[0]?.toUpperCase() || 'U'}
                     </Avatar>
                     <Typography variant='body2' color='text.secondary'>
                        Created by {album.creator.displayName}
                     </Typography>
                  </Box>
               )}
               <Typography variant='body2' color='text.secondary'>
                  {formatDate(album.createdAt)}
               </Typography>
            </Box>
         </AlbumHeader>

         {/* Photos Grid */}
         {photos.length > 0 ? (
            <Grid container spacing={2}>
               {photos.map((photo, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id}>
                     <PhotoCard onClick={() => openPhotoModal(index)}>
                        <CardMedia
                           component='img'
                           height='200'
                           image={photo.thumbnailUrl}
                           alt={photo.title}
                           sx={{ objectFit: 'cover' }}
                        />
                     </PhotoCard>
                  </Grid>
               ))}
            </Grid>
         ) : (
            <Alert severity='info'>This album doesn't have any photos yet.</Alert>
         )}

         {/* Photo Modal */}
         <PhotoModal open={modalOpen} onClose={closePhotoModal} maxWidth={false}>
            <DialogContent sx={{ p: 0, position: 'relative' }}>
               <IconButton
                  onClick={closePhotoModal}
                  sx={{
                     position: 'absolute',
                     top: 16,
                     right: 16,
                     color: 'white',
                     backgroundColor: 'rgba(0, 0, 0, 0.5)',
                     zIndex: 1,
                     '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                     },
                  }}
               >
                  <CloseIcon />
               </IconButton>

               {photos.length > 1 && (
                  <>
                     <IconButton
                        onClick={goToPrevPhoto}
                        sx={{
                           position: 'absolute',
                           left: 16,
                           top: '50%',
                           transform: 'translateY(-50%)',
                           color: 'white',
                           backgroundColor: 'rgba(0, 0, 0, 0.5)',
                           zIndex: 1,
                           '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                           },
                        }}
                     >
                        <ArrowBackIosIcon />
                     </IconButton>

                     <IconButton
                        onClick={goToNextPhoto}
                        sx={{
                           position: 'absolute',
                           right: 16,
                           top: '50%',
                           transform: 'translateY(-50%)',
                           color: 'white',
                           backgroundColor: 'rgba(0, 0, 0, 0.5)',
                           zIndex: 1,
                           '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                           },
                        }}
                     >
                        <ArrowForwardIosIcon />
                     </IconButton>
                  </>
               )}

               <img
                  src={photos[currentPhotoIndex]?.url}
                  alt={photos[currentPhotoIndex]?.title}
                  style={{
                     width: '100%',
                     height: 'auto',
                     maxHeight: '90vh',
                     objectFit: 'contain',
                  }}
               />
            </DialogContent>
         </PhotoModal>
      </StyledContainer>
   );
};

export default PublicAlbumViewer; 